/**
 * Which score a product tile/row shows, and how to colour it. Kept out of the
 * two view components so that both resolve it identically — the same product
 * must not read differently in card view than in table view — and so each of
 * those files only exports components (fast refresh requires it).
 */

import type { Product } from '~/features/catalog-operations/types/catalog-operations.types';

/** Score bands. Below 55 is the set worth opening first. */
export function scoreTone(score: number): string {
  if (score >= 80) return 'text-success';
  if (score >= 55) return 'text-warning';
  return 'text-error';
}

export interface ProductScoreBadge {
  /** 0–100, unrounded. Round at the render site. */
  value: number;
  /** Points gained (or lost) against the pre-enrichment baseline; null when
   *  there is no baseline to compare against, in which case no lift is shown. */
  delta: number | null;
  /** The pre-enrichment baseline itself, 0–100; null when the product has none.
   *  Lets a render site show "before → after" rather than only the lift. */
  before: number | null;
  /** What the number actually measures — the two sources are NOT interchangeable. */
  label: string;
}

/**
 * Resolve the score to display for a product, or `null` for no score at all.
 *
 * Enrichment score plus its lift is the signal an operator wants: "this product
 * got better, by this much". It needs BOTH halves — a post score with no pre
 * score yields no delta, so the badge degrades to the plain score rather than
 * inventing a baseline.
 *
 * It deliberately does NOT fall back to curation `completenessScore`. That is
 * catalog coverage — whether fields are populated — not enrichment quality, and
 * it is near-constant across a feed, so a column of identical numbers read as a
 * meaningful score while telling an operator nothing they could act on. Showing
 * no score is the honest answer until the enrichment figures arrive.
 *
 * The enrichment path stays wired, so the score returns on its own the moment
 * the curation list response carries `enrichment_score` / `pre_enrichment_score`
 * — no UI change needed then.
 */
export function scoreBadge(product: Product): ProductScoreBadge | null {
  const { enrichmentScore, preEnrichmentScore } = product;
  if (enrichmentScore == null) return null;
  return {
    value: enrichmentScore,
    delta: preEnrichmentScore == null ? null : enrichmentScore - preEnrichmentScore,
    before: preEnrichmentScore ?? null,
    label: 'Enrichment score',
  };
}
